import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, CheckCircle2, Circle, MessageSquare, ExternalLink } from "lucide-react";

export default function CassettoAdmin() {
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [openAnswers, setOpenAnswers] = useState(null); // question id
  const [answers, setAnswers] = useState([]);

  const load = () => api.get("/cassetto/questions").then((r) => setQuestions(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post("/cassetto/questions", { text: text.trim() });
      setText("");
      await load();
      toast.success("Domanda creata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const activate = async (id) => {
    await api.put(`/cassetto/questions/${id}/activate`);
    await load();
    toast.success("Domanda attivata");
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare la domanda e tutte le sue risposte?")) return;
    await api.delete(`/cassetto/questions/${id}`);
    if (openAnswers === id) setOpenAnswers(null);
    await load();
    toast.success("Domanda eliminata");
  };

  const toggleAnswers = async (id) => {
    if (openAnswers === id) { setOpenAnswers(null); return; }
    const r = await api.get(`/cassetto/questions/${id}/answers`);
    setAnswers(r.data || []);
    setOpenAnswers(id);
  };

  const deleteAnswer = async (aid) => {
    await api.delete(`/cassetto/answers/${aid}`);
    setAnswers((a) => a.filter((x) => x.id !== aid));
    load();
    toast.success("Risposta eliminata");
  };

  return (
    <div className="relative z-10 min-h-screen bg-[#F9F8F6]">
      <div className="border-b border-[#E7E5E4] bg-[#F2F0EB]">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F]"><ArrowLeft size={15} /> Torna</Link>
          <Link to="/il-cassetto" target="_blank" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F]"><ExternalLink size={15} /> Vedi la sezione</Link>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <p className="label-ui text-[#9F583F] mb-2">Il Cassetto</p>
        <h1 className="font-display text-4xl tracking-tight text-[#1C1917] mb-10">Domande della giraffa</h1>

        <form onSubmit={create} className="border border-[#E7E5E4] bg-[#F2F0EB] p-6 mb-10">
          <label className="font-display text-xl text-[#1C1917] block mb-3">Nuova domanda</label>
          <textarea
            data-testid="cassetto-q-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            maxLength={500}
            placeholder="Es. Qual è il libro che ti ha cambiato la vita?"
            className="w-full font-ui text-sm border border-[#E7E5E4] bg-white px-3 py-2 focus:outline-none focus:border-[#9F583F]"
          />
          <button type="submit" disabled={saving} data-testid="cassetto-q-create" className="mt-4 inline-flex items-center gap-2 bg-[#9F583F] text-white label-ui px-5 py-2.5 hover:bg-[#1C1917] transition-colors disabled:opacity-60">
            <Plus size={14} /> {saving ? "Salvataggio…" : "Crea domanda"}
          </button>
          <p className="label-ui text-[#A8A29E] mt-3">La prima domanda diventa attiva in automatico. Solo una domanda è attiva alla volta.</p>
        </form>

        <div className="space-y-3" data-testid="cassetto-questions-list">
          {questions.map((q) => (
            <div key={q.id} className="border border-[#E7E5E4] bg-white" data-testid={`cassetto-q-${q.id}`}>
              <div className="p-5 flex items-start gap-4">
                <button
                  onClick={() => activate(q.id)}
                  data-testid={`cassetto-q-activate-${q.id}`}
                  title={q.active ? "Attiva" : "Rendi attiva"}
                  className={`shrink-0 mt-1 ${q.active ? "text-[#6F7F35]" : "text-[#C9C2BA] hover:text-[#9F583F]"}`}
                >
                  {q.active ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className="font-display text-lg text-[#1C1917]">{q.text}</p>
                  <div className="flex items-center gap-4 mt-2">
                    {q.active && <span className="label-ui text-white bg-[#6F7F35] px-2 py-0.5 rounded-full">Attiva</span>}
                    <button onClick={() => toggleAnswers(q.id)} data-testid={`cassetto-q-answers-${q.id}`} className="inline-flex items-center gap-1.5 label-ui text-[#57534E] hover:text-[#9F583F]">
                      <MessageSquare size={13} /> {q.answer_count} risposte
                    </button>
                  </div>
                </div>
                <button onClick={() => remove(q.id)} data-testid={`cassetto-q-delete-${q.id}`} className="text-[#A8A29E] hover:text-red-600 shrink-0"><Trash2 size={16} /></button>
              </div>

              {openAnswers === q.id && (
                <div className="border-t border-[#E7E5E4] bg-[#FBFAF8] p-5 space-y-3" data-testid={`cassetto-answers-${q.id}`}>
                  {answers.length === 0 ? (
                    <p className="font-body italic text-[#57534E]">Nessuna risposta ancora.</p>
                  ) : (
                    answers.map((a) => (
                      <div key={a.id} className="flex items-start gap-3 bg-white border border-[#E7E5E4] p-3">
                        <p className="font-body text-sm text-[#292524] flex-1 whitespace-pre-wrap">{a.text}</p>
                        <button onClick={() => deleteAnswer(a.id)} data-testid={`cassetto-answer-delete-${a.id}`} className="text-[#A8A29E] hover:text-red-600 shrink-0"><Trash2 size={14} /></button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          ))}
          {questions.length === 0 && <p className="font-body italic text-[#57534E]">Nessuna domanda ancora.</p>}
        </div>
      </div>
    </div>
  );
}
